import { SAVE_VERSION, createInitialState, type GameState } from '../state/gameState';

type AnySave = Record<string, unknown> & { saveVersion?: number };

/**
 * Ordered migrations: MIGRATIONS[v] upgrades a v→(v+1) save.
 * NEVER delete an entry — players carry saves on old versions. This is the
 * seam that makes shipping updates after she has a save safe (non-optional).
 */
const MIGRATIONS: Array<(s: AnySave) => AnySave> = [
  // index 0: v0 → v1 baseline (first shipped schema).
  (s) => ({ ...s, saveVersion: 1 }),
  // index 1: v1 → v2 — Tier 2 added weeds + chopped-tree tracking.
  (s) => ({
    ...s,
    saveVersion: 2,
    weeds: Array.isArray(s.weeds) ? s.weeds : [],
    chopped: Array.isArray(s.chopped) ? s.chopped : [],
  }),
  // index 2: v2 → v3 — livestock (chickens) + eggs.
  (s) => ({
    ...s,
    saveVersion: 3,
    animals: Array.isArray(s.animals) ? s.animals : [],
    eggs: Array.isArray(s.eggs) ? s.eggs : [],
  }),
  // index 3: v3 → v4 — weather.
  (s) => ({ ...s, saveVersion: 4, weather: s.weather === 'rain' ? 'rain' : 'clear' }),
  // index 4: v4 → v5 — house storage chest.
  (s) => ({ ...s, saveVersion: 5, storage: Array.isArray(s.storage) ? s.storage : [] }),
  // index 5: v5 → v6 — goal tracking.
  (s) => ({ ...s, saveVersion: 6, goalsClaimed: Array.isArray(s.goalsClaimed) ? s.goalsClaimed : [] }),
  // index 6: v6 → v7 — mailbox.
  (s) => ({ ...s, saveVersion: 7, mail: s.mail ?? null }),
  // index 7: v7 → v8 — Clara friendship.
  (s) => ({ ...s, saveVersion: 8, claraHearts: typeof s.claraHearts === 'number' ? s.claraHearts : 0 }),
  // index 8: v8 → v9 — build-mode building positions.
  (s) => ({ ...s, saveVersion: 9, buildings: s.buildings && typeof s.buildings === 'object' ? s.buildings : {} }),
  // index 9: v9 → v10 — pet type (cat/dog).
  (s) => ({ ...s, saveVersion: 10, petType: s.petType === 'dog' ? 'dog' : 'cat' }),
  // index 10: v10 → v11 — collection log.
  (s) => ({ ...s, saveVersion: 11, collected: s.collected && typeof s.collected === 'object' ? s.collected : {} }),
  // index 11: v11 → v12 — custom pet name.
  (s) => ({ ...s, saveVersion: 12, petName: typeof s.petName === 'string' ? s.petName : '' }),
  // index 12: v12 → v13 — pet bond.
  (s) => ({ ...s, saveVersion: 13, petHearts: typeof s.petHearts === 'number' ? s.petHearts : 0 }),
  // index 13: v13 → v14 — milestone journal.
  (s) => ({ ...s, saveVersion: 14, journal: Array.isArray(s.journal) ? s.journal : [] }),
  // index 14: v14 → v15 — house upgrade tier.
  (s) => ({ ...s, saveVersion: 15, houseLevel: typeof s.houseLevel === 'number' ? s.houseLevel : 0 }),
  // index 15: v15 → v16 — interior decorations.
  (s) => ({ ...s, saveVersion: 16, interiorDecor: Array.isArray(s.interiorDecor) ? s.interiorDecor : [] }),
  // index 16: v16 → v17 — barn upgrade tier.
  (s) => ({ ...s, saveVersion: 17, barnLevel: typeof s.barnLevel === 'number' ? s.barnLevel : 0 }),
];

export interface MigrationResult {
  state: GameState;
  /** The version we migrated from, or null if already current. */
  migratedFrom: number | null;
  ok: boolean;
  error?: string;
}

export function migrateSave(raw: unknown): MigrationResult {
  if (raw == null || typeof raw !== 'object') {
    return { state: createInitialState(), migratedFrom: null, ok: false, error: 'empty or non-object save' };
  }

  let save = { ...(raw as AnySave) };
  const from = typeof save.saveVersion === 'number' ? save.saveVersion : 0;

  if (from > SAVE_VERSION) {
    // Newer than this build understands — refuse rather than corrupt it.
    return {
      state: createInitialState(),
      migratedFrom: from,
      ok: false,
      error: `save v${from} is newer than supported v${SAVE_VERSION}`,
    };
  }

  let v = from;
  while (v < SAVE_VERSION) {
    const step = MIGRATIONS[v];
    if (!step) break;
    save = step(save);
    v += 1;
  }
  save.saveVersion = SAVE_VERSION;

  return {
    state: save as unknown as GameState,
    migratedFrom: from === SAVE_VERSION ? null : from,
    ok: true,
  };
}
